import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'
import { Cookies } from 'react-cookie'
import { get } from 'lodash'
import { cookieOptions, parseSessionToken, removeBearerFromToken } from './CookieUtils'

export const REFRESH_URL = '/api/v1/session/refresh'
const REFRESH_HINT_COOKIE = 'sdv_has_refresh'
const NO_RETRY_URLS = [REFRESH_URL, '/api/v1/login', '/api/v1/logout']
const MAX_JWT_LIFETIME_S = 24 * 3600
const OTHER_TAB_GRACE_MS = 1500
const REFRESH_TIMEOUT_MS = 10000

type Listener = (token: string | undefined) => void
const listeners = new Set<Listener>()
let inFlight: Promise<string | undefined> | undefined

interface Claims { iat?: number, exp?: number }

const claimsOf = (token?: string): Claims | undefined => {
  const payload = removeBearerFromToken(token)?.split('.')[1]
  if (payload === undefined) return undefined

  try {
    return JSON.parse(window.atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
  } catch {
    return undefined
  }
}

export const isLegacyToken = (token?: string): boolean => {
  const claims = claimsOf(token)
  return claims?.iat !== undefined && claims.exp !== undefined && claims.exp - claims.iat > MAX_JWT_LIFETIME_S
}

export const isExpiredToken = (token?: string): boolean => {
  const exp = claimsOf(token)?.exp
  return exp !== undefined && exp * 1000 <= Date.now()
}

export const currentSessionToken = (): string | undefined => {
  const token = parseSessionToken((new Cookies()).get('sessionToken'))
  return token === '' ? undefined : token
}

export const hasRefreshHint = (): boolean => (new Cookies()).get(REFRESH_HINT_COOKIE, { doNotParse: true }) === '1'

export const isUsableToken = (token?: string): boolean =>
  token !== undefined && token !== '' && !isExpiredToken(token) && !isLegacyToken(token)

// Called with the new token after a refresh, or with undefined when the server refused it.
export const onSessionRefresh = (listener: Listener): () => void => {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

const usableTokenFromOtherTab = (staleToken?: string): string | undefined => {
  const latest = currentSessionToken()
  return latest !== staleToken && isUsableToken(latest) ? latest : undefined
}

const postRefresh = async (staleToken?: string): Promise<string | undefined> => {
  const headers: Record<string, string> = { 'X-Requested-With': 'XMLHttpRequest' }
  if (staleToken !== undefined && isLegacyToken(staleToken)) headers.Authorization = staleToken
  const response = await axios({ method: 'POST', url: REFRESH_URL, headers, timeout: REFRESH_TIMEOUT_MS })
  return get(response, 'headers.authorization', undefined)
}

const refreshOnce = async (staleToken?: string): Promise<string | undefined> => {
  const fromOtherTab = usableTokenFromOtherTab(staleToken)
  if (fromOtherTab !== undefined) return fromOtherTab

  try {
    return await postRefresh(staleToken)
  } catch (err) {
    const status = get(err, 'response.status', undefined)
    if (status !== 401 && status !== 409) throw err
    if (status === 401 && (staleToken === undefined || staleToken === '')) return undefined
  }
  // Another tab may have used the same refresh token a moment ago. The browser
  // then has its new cookie, so wait for that tab and try once more.
  await new Promise(resolve => setTimeout(resolve, OTHER_TAB_GRACE_MS))
  const adopted = usableTokenFromOtherTab(staleToken)
  if (adopted !== undefined) return adopted
  try {
    return await postRefresh(staleToken)
  } catch (err) {
    if (get(err, 'response.status', undefined) !== 401) throw err
    return undefined
  }
}

// Returns a new session token, or undefined when the session has ended. Rejects
// on network errors, so callers can keep the user logged in.
export const refreshSession = async (staleToken?: string): Promise<string | undefined> => {
  if (inFlight !== undefined) return await inFlight

  inFlight = refreshOnce(staleToken).then((token) => {
    if (token !== undefined) (new Cookies()).set('sessionToken', token, cookieOptions())
    listeners.forEach(listener => listener(token))
    return token
  }).finally(() => { inFlight = undefined })
  return await inFlight
}

interface RetriedConfig extends AxiosRequestConfig { sdvRefreshed?: boolean }

export const addRefreshInterceptor = (instance: AxiosInstance): void => {
  instance.interceptors?.response?.use((response) => response, async (error: AxiosError) => {
    const config = error.config as RetriedConfig | undefined
    const authorization: unknown = get(config, 'headers.Authorization', undefined)
    const bearer = typeof authorization === 'string' && authorization.startsWith('Bearer ')
    const retryable = config !== undefined && config.sdvRefreshed !== true &&
      get(error, 'response.status', undefined) === 401 &&
      get(error, 'response.data.error_key', undefined) !== 'tos_required' &&
      (bearer || authorization === undefined) &&
      !NO_RETRY_URLS.some(url => String(config.url).includes(url))
    if (!retryable) return await Promise.reject(error)

    const token = await refreshSession(bearer ? authorization : currentSessionToken()).catch(() => undefined)
    if (token === undefined) return await Promise.reject(error)

    config.sdvRefreshed = true
    config.headers = { ...config.headers, Authorization: token }
    return await instance(config)
  })
}

addRefreshInterceptor(axios)
